import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMembers } from "@/hooks/use-members";
import { useTranslation } from "react-i18next";

export type InboxRouting =
  | { mode: "unassigned" }
  | { mode: "round_robin"; userIds: string[] }
  | { mode: "sticky_by_participant" }
  | { mode: "fixed"; userId: string };

interface RoutingSettingsProps {
  brandId: string;
  canManage: boolean;
}

const BASE = "/api";

async function fetchRouting(brandId: string): Promise<InboxRouting> {
  const r = await fetch(`${BASE}/brands/${brandId}/inbox/routing`, {
    credentials: "include",
  });
  if (!r.ok) throw new Error("Failed to load routing");
  const json = (await r.json()) as { data: { routing: InboxRouting } };
  return json.data.routing;
}

async function saveRouting(
  brandId: string,
  routing: InboxRouting,
): Promise<InboxRouting> {
  const r = await fetch(`${BASE}/brands/${brandId}/inbox/routing`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ routing }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body?.error?.message ?? "Save failed");
  }
  const json = (await r.json()) as { data: { routing: InboxRouting } };
  return json.data.routing;
}

/**
 * Inbox routing config. Owner/Admin only. Other roles see a read-only view.
 * Renders inside the Inbox page header (or settings panel).
 */
export function InboxRoutingSettings({ brandId, canManage }: RoutingSettingsProps) {
  const { t } = useTranslation("inbox");
  const qc = useQueryClient();
  const { data: routing } = useQuery({
    queryKey: ["inbox-routing", brandId],
    queryFn: () => fetchRouting(brandId),
    enabled: !!brandId,
  });
  const { data: members } = useMembers(brandId);
  const [mode, setMode] = useState<InboxRouting["mode"]>("unassigned");
  const [fixedUserId, setFixedUserId] = useState<string>("");
  const [rrUserIds, setRrUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (!routing) return;
    setMode(routing.mode);
    if (routing.mode === "fixed") setFixedUserId(routing.userId);
    if (routing.mode === "round_robin") setRrUserIds(routing.userIds);
  }, [routing]);

  const mutation = useMutation({
    mutationFn: (next: InboxRouting) => saveRouting(brandId, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox-routing", brandId] }),
  });

  if (!routing) return null;

  function buildNext(): InboxRouting | null {
    if (mode === "unassigned") return { mode: "unassigned" };
    if (mode === "sticky_by_participant") return { mode: "sticky_by_participant" };
    if (mode === "fixed") {
      if (!fixedUserId) return null;
      return { mode: "fixed", userId: fixedUserId };
    }
    if (mode === "round_robin") {
      if (rrUserIds.length === 0) return null;
      return { mode: "round_robin", userIds: rrUserIds };
    }
    return null;
  }

  const next = buildNext();
  const dirty = JSON.stringify(next) !== JSON.stringify(routing);

  return (
    <div className="rounded-lg border border-border bg-surface-1 p-4 text-[13px]">
      <h3 className="text-sm font-semibold mb-2">{t("settings.routing.title")}</h3>
      <p className="text-muted-foreground text-xs mb-3">
        {t("settings.routing.description")}
      </p>

      <label className="block mb-2 text-xs font-medium">{t("settings.routing.modeLabel")}</label>
      <select
        className="w-full rounded border border-border bg-surface-0 px-2 py-1.5 mb-3"
        value={mode}
        onChange={(e) => setMode(e.target.value as InboxRouting["mode"])}
        disabled={!canManage || mutation.isPending}
      >
        <option value="unassigned">{t("settings.routing.modes.unassigned")}</option>
        <option value="round_robin">{t("settings.routing.modes.round_robin")}</option>
        <option value="sticky_by_participant">{t("settings.routing.modes.sticky_by_participant")}</option>
        <option value="fixed">{t("settings.routing.modes.fixed")}</option>
      </select>

      {mode === "fixed" && (
        <div className="mb-3">
          <label className="block mb-1 text-xs font-medium">{t("settings.routing.assigneeLabel")}</label>
          <select
            className="w-full rounded border border-border bg-surface-0 px-2 py-1.5"
            value={fixedUserId}
            onChange={(e) => setFixedUserId(e.target.value)}
            disabled={!canManage || mutation.isPending}
          >
            <option value="">{t("settings.routing.selectMember")}</option>
            {members?.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name ?? m.email} ({m.role})
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === "round_robin" && (
        <div className="mb-3">
          <label className="block mb-1 text-xs font-medium">
            {t("settings.routing.membersInRotation")}
          </label>
          <div className="space-y-1 max-h-48 overflow-auto rounded border border-border bg-surface-0 p-2">
            {members?.map((m) => (
              <label key={m.userId} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rrUserIds.includes(m.userId)}
                  disabled={!canManage || mutation.isPending}
                  onChange={(e) => {
                    setRrUserIds((prev) =>
                      e.target.checked
                        ? [...prev, m.userId]
                        : prev.filter((u) => u !== m.userId),
                    );
                  }}
                />
                <span>
                  {m.name ?? m.email}{" "}
                  <span className="text-muted-foreground">({m.role})</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {canManage && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
            disabled={!dirty || !next || mutation.isPending}
            onClick={() => next && mutation.mutate(next)}
          >
            {mutation.isPending ? t("settings.routing.saving") : t("settings.routing.save")}
          </button>
          {mutation.isError && (
            <span className="text-xs text-destructive">
              {(mutation.error as Error).message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
